'use client'

/**
 * ClearanceInput — shows that bus/truck routing is bridge- and weight-aware,
 * and lets the user replace the preset height with their own vehicle's.
 *
 * Only rendered for vehicles with a `clearance` envelope (cars route with
 * Mapbox's passenger-car defaults, so there's nothing to show).
 *
 * UX mirrors CustomMpgInput:
 *   - Collapsed: "Routing for 12′6″ · 54,500 lb — avoids low bridges &
 *     weight limits" plus Override / Reset.
 *   - Expanded: a free-text height field that accepts how people write
 *     heights (13'6", 13 6, 13.5). Valid values save as you type; the store
 *     persists them per vehicle and redraws the route preview.
 *
 * There is deliberately no "off" switch: a bus routed without its height is a
 * route the bus can't drive.
 */

import { Check, Pencil, RotateCcw, ShieldCheck } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { track } from '@/lib/analytics'
import { formatHeightFeet, formatWeightPounds, parseHeightFeet } from '@/lib/clearance'
import type { VehicleId } from '@/lib/types'
import { getVehicle } from '@/lib/vehicles'

interface ClearanceInputProps {
	vehicleId: VehicleId
	/** Custom height in meters, or null for the preset. */
	value: number | null
	onChange: (meters: number | null) => void
}

export function ClearanceInput({ vehicleId, value, onChange }: ClearanceInputProps) {
	const clearance = getVehicle(vehicleId).clearance
	const [isEditing, setIsEditing] = useState(false)
	const [inputValue, setInputValue] = useState('')

	// Vehicle switches restore that vehicle's own saved height (or null) —
	// collapse the editor so it never shows another vehicle's draft.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset on vehicle change only
	useEffect(() => {
		setIsEditing(false)
		setInputValue('')
	}, [vehicleId])

	if (!clearance) return null

	const presetHeight = formatHeightFeet(clearance.heightM)
	const hasCustom = value !== null
	const height = formatHeightFeet(value ?? clearance.heightM)
	const inputValid = parseHeightFeet(inputValue) !== null

	const handleChange = (raw: string) => {
		setInputValue(raw)
		const meters = parseHeightFeet(raw)
		if (meters === null) return
		if (value === null) track('OverrodeHeight', { vehicle: vehicleId, height_m: meters })
		onChange(meters)
	}

	const handleReset = () => {
		setIsEditing(false)
		setInputValue('')
		onChange(null)
	}

	if (!isEditing) {
		return (
			<div className="flex items-start justify-between gap-2">
				<div className="flex min-w-0 gap-2 text-sm">
					<ShieldCheck className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />
					<div className="min-w-0">
						<div>
							<span className="text-text-secondary">Routing for </span>
							<span className="font-mono font-semibold text-text-primary">{height}</span>
							<span className="text-text-secondary"> · </span>
							<span className="whitespace-nowrap font-mono text-text-primary">
								{formatWeightPounds(clearance.weightTonnes)}
							</span>
							<span
								className={`ml-1.5 text-[10px] uppercase tracking-wider ${hasCustom ? 'text-brand' : 'text-text-muted'}`}
							>
								{hasCustom ? 'custom' : 'preset'}
							</span>
						</div>
						<div className="text-[11px] text-text-muted">
							Avoids low bridges &amp; weight limits
						</div>
					</div>
				</div>
				<div className="flex shrink-0 items-center gap-1.5">
					<button
						type="button"
						onClick={() => {
							setInputValue(hasCustom ? height : '')
							setIsEditing(true)
						}}
						className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium text-text-secondary transition-colors hover:border-brand/60 hover:text-text-primary"
					>
						<Pencil className="size-3" aria-hidden />
						{hasCustom ? 'Edit' : 'Override'}
					</button>
					{hasCustom && (
						<button
							type="button"
							onClick={handleReset}
							className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium text-text-muted transition-colors hover:border-error-fg/60 hover:text-error-fg"
							title={`Reset to preset (${presetHeight})`}
						>
							<RotateCcw className="size-3" aria-hidden />
							Reset
						</button>
					)}
				</div>
			</div>
		)
	}

	return (
		<fieldset className="rounded-md border border-brand/30 bg-brand/5 px-3 py-2.5">
			<legend className="flex w-full items-center justify-between text-sm">
				<span className="font-semibold text-text-primary">Vehicle height</span>
				<button
					type="button"
					onClick={handleReset}
					className="flex items-center gap-1 text-xs text-text-muted transition-colors hover:text-text-primary"
				>
					<RotateCcw className="size-3" aria-hidden />
					Use preset ({presetHeight})
				</button>
			</legend>
			<div className="mt-2 flex items-center gap-2">
				<Input
					type="text"
					inputMode="decimal"
					value={inputValue}
					onChange={(e) => handleChange(e.target.value)}
					placeholder={`e.g. 13'6"`}
					className="flex-1 font-mono"
					aria-label="Vehicle height in feet and inches"
					aria-invalid={inputValue !== '' && !inputValid}
				/>
				<Button
					type="button"
					variant="default"
					size="sm"
					onClick={() => setIsEditing(false)}
					disabled={!inputValid}
					title={
						inputValid ? 'Close editor (value is already saved)' : 'Enter a height from 6 to 15 ft'
					}
				>
					<Check className="size-3.5" aria-hidden />
					Done
				</Button>
			</div>
			<p className="mt-1.5 text-[10px] text-text-muted">
				Measure to the highest point — A/C pods, satellite domes. Saved automatically as you type.
			</p>
		</fieldset>
	)
}
